import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { nanoid } from 'nanoid';
import type { App, ConnectionPlan, PlanApp, PlanConnection, Position } from '../types';

interface PlanningState {
  currentPlan: ConnectionPlan | null;
  savedPlans: ConnectionPlan[];
  
  // Plan management
  createNewPlan: (name?: string) => void;
  loadPlan: (planId: string) => void;
  savePlan: () => void;
  clearPlan: () => void;
  deletePlan: (planId: string) => void;
  
  // Plan metadata
  updatePlanMetadata: (updates: Partial<Pick<ConnectionPlan, 'name' | 'description'>>) => void;
  
  // App management
  addAppToPlan: (planApp: Omit<PlanApp, 'id'>) => void;
  removeAppFromPlan: (appId: string) => void;
  updateAppPosition: (appId: string, position: Position) => void;
  
  // Connection management
  addConnection: (connection: Omit<PlanConnection, 'id'>) => void;
  removeConnection: (connectionId: string) => void;
  updateConnection: (connectionId: string, updates: Partial<PlanConnection>) => void;
  
  // Utilities
  getConnectedApps: (appId: string) => string[];
  validatePlan: () => { isValid: boolean; errors: string[] };
}

export const usePlanningStore = create<PlanningState>()(
  persist(
    (set, get) => ({
      currentPlan: null,
      savedPlans: [],

      createNewPlan: (name = 'New Connection Plan') => {
        const newPlan: ConnectionPlan = {
          id: nanoid(),
          name,
          description: '',
          apps: [],
          connections: [],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };
        
        set({ currentPlan: newPlan });
      },

      loadPlan: (planId: string) => {
        const { savedPlans } = get();
        const plan = savedPlans.find(p => p.id === planId);
        
        if (plan) {
          set({ currentPlan: { ...plan } });
        }
      },

      savePlan: () => {
        const { currentPlan, savedPlans } = get();
        
        if (!currentPlan) return;
        
        const updatedPlan = {
          ...currentPlan,
          updatedAt: new Date().toISOString(),
        };
        
        const existingIndex = savedPlans.findIndex(p => p.id === currentPlan.id);
        
        if (existingIndex >= 0) {
          // Update existing plan
          const newSavedPlans = [...savedPlans];
          newSavedPlans[existingIndex] = updatedPlan;
          set({ savedPlans: newSavedPlans, currentPlan: updatedPlan });
        } else {
          // Save new plan
          set({ 
            savedPlans: [...savedPlans, updatedPlan],
            currentPlan: updatedPlan 
          });
        }
      },

      clearPlan: () => {
        set({ currentPlan: null });
      },

      deletePlan: (planId: string) => {
        const { savedPlans, currentPlan } = get();
        const newSavedPlans = savedPlans.filter(p => p.id !== planId);
        
        set({ 
          savedPlans: newSavedPlans,
          currentPlan: currentPlan?.id === planId ? null : currentPlan
        });
      },

      updatePlanMetadata: (updates) => {
        const { currentPlan } = get();
        
        if (!currentPlan) return;
        
        set({
          currentPlan: {
            ...currentPlan,
            ...updates,
            updatedAt: new Date().toISOString(),
          }
        });
      },

      addAppToPlan: (planApp) => {
        const { currentPlan } = get();
        
        if (!currentPlan) {
          // Create new plan if none exists
          get().createNewPlan();
          return get().addAppToPlan(planApp);
        }
        
        // Check if app already exists in plan
        const existingApp = currentPlan.apps.find(a => a.app.id === planApp.app.id);
        if (existingApp) return;
        
        const newPlanApp: PlanApp = {
          ...planApp,
          id: nanoid(),
        };
        
        set({
          currentPlan: {
            ...currentPlan,
            apps: [...currentPlan.apps, newPlanApp],
            updatedAt: new Date().toISOString(),
          }
        });
      },

      removeAppFromPlan: (appId: string) => {
        const { currentPlan } = get();
        
        if (!currentPlan) return;
        
        // Remove app and all its connections
        const newApps = currentPlan.apps.filter(a => a.app.id !== appId);
        const newConnections = currentPlan.connections.filter(
          c => c.fromAppId !== appId && c.toAppId !== appId
        );
        
        set({
          currentPlan: {
            ...currentPlan,
            apps: newApps,
            connections: newConnections,
            updatedAt: new Date().toISOString(),
          }
        });
      },

      updateAppPosition: (appId: string, position: Position) => {
        const { currentPlan } = get();
        
        if (!currentPlan) return;
        
        const newApps = currentPlan.apps.map(planApp => 
          planApp.app.id === appId 
            ? { ...planApp, position }
            : planApp
        );
        
        set({
          currentPlan: {
            ...currentPlan,
            apps: newApps,
            updatedAt: new Date().toISOString(),
          }
        });
      },

      addConnection: (connection) => {
        const { currentPlan } = get();
        
        if (!currentPlan) return;
        
        // Check if connection already exists
        const existingConnection = currentPlan.connections.find(
          c => c.fromAppId === connection.fromAppId && c.toAppId === connection.toAppId
        );
        
        if (existingConnection) return;
        
        const newConnection: PlanConnection = {
          ...connection,
          id: connection.id || nanoid(),
        };
        
        set({
          currentPlan: {
            ...currentPlan,
            connections: [...currentPlan.connections, newConnection],
            updatedAt: new Date().toISOString(),
          }
        });
      },

      removeConnection: (connectionId: string) => {
        const { currentPlan } = get();
        
        if (!currentPlan) return;
        
        const newConnections = currentPlan.connections.filter(c => c.id !== connectionId);
        
        set({
          currentPlan: {
            ...currentPlan,
            connections: newConnections,
            updatedAt: new Date().toISOString(),
          }
        });
      },

      updateConnection: (connectionId: string, updates: Partial<PlanConnection>) => {
        const { currentPlan } = get();
        
        if (!currentPlan) return;
        
        const newConnections = currentPlan.connections.map(connection =>
          connection.id === connectionId
            ? { ...connection, ...updates }
            : connection
        );
        
        set({
          currentPlan: {
            ...currentPlan,
            connections: newConnections,
            updatedAt: new Date().toISOString(),
          }
        });
      },

      getConnectedApps: (appId: string) => {
        const { currentPlan } = get();
        
        if (!currentPlan) return [];
        
        const connectedIds = new Set<string>();
        
        currentPlan.connections.forEach(connection => {
          if (connection.fromAppId === appId) {
            connectedIds.add(connection.toAppId);
          }
          if (connection.toAppId === appId) {
            connectedIds.add(connection.fromAppId);
          }
        });
        
        return Array.from(connectedIds);
      },

      validatePlan: () => {
        const { currentPlan } = get();
        const errors: string[] = [];
        
        if (!currentPlan) {
          return { isValid: false, errors: ['No plan loaded'] };
        }
        
        if (currentPlan.apps.length === 0) {
          errors.push('Plan must contain at least one app');
        }
        
        if (currentPlan.apps.length === 1) {
          errors.push('Plan should contain at least two apps for meaningful connections');
        }
        
        if (currentPlan.connections.length === 0 && currentPlan.apps.length > 1) {
          errors.push('Apps should be connected to create a workflow');
        }
        
        // Check for orphaned connections
        currentPlan.connections.forEach(connection => {
          const fromAppExists = currentPlan.apps.some(a => a.app.id === connection.fromAppId);
          const toAppExists = currentPlan.apps.some(a => a.app.id === connection.toAppId);
          
          if (!fromAppExists || !toAppExists) {
            errors.push(`Connection ${connection.id} references non-existent apps`);
          }
        });
        
        // Check for circular dependencies (basic check)
        const hasCircularDependency = () => {
          const visited = new Set<string>();
          const recursionStack = new Set<string>();
          
          const hasCycle = (appId: string): boolean => {
            if (recursionStack.has(appId)) return true;
            if (visited.has(appId)) return false;
            
            visited.add(appId);
            recursionStack.add(appId);
            
            const outgoingConnections = currentPlan.connections.filter(c => c.fromAppId === appId);
            for (const connection of outgoingConnections) {
              if (hasCycle(connection.toAppId)) return true;
            }
            
            recursionStack.delete(appId);
            return false;
          };
          
          for (const planApp of currentPlan.apps) {
            if (hasCycle(planApp.app.id)) return true;
          }
          
          return false;
        };
        
        if (hasCircularDependency()) {
          errors.push('Plan contains circular dependencies');
        }
        
        return {
          isValid: errors.length === 0,
          errors
        };
      },
    }),
    {
      name: 'planning-store',
      partialize: (state) => ({
        savedPlans: state.savedPlans,
        // Don't persist currentPlan to avoid loading stale data
      }),
    }
  )
);